import { useEffect, useState, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin, Navigation, Loader2 } from 'lucide-react';
import { useI18n } from '../../i18n';

// Fix Leaflet default icon issue - use CDN URLs for reliability
const DefaultIcon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

interface LocationPickerMapProps {
  initialLat?: number;
  initialLng?: number;
  onLocationSelect: (lat: number, lng: number, address?: string) => void;
  height?: string;
}

// Component to handle map clicks and update marker position
function MapClickHandler({ 
  position, 
  setPosition, 
  onLocationSelect 
}: { 
  position: [number, number] | null;
  setPosition: (pos: [number, number]) => void;
  onLocationSelect: (lat: number, lng: number, address?: string) => void;
}) {
  const map = useMapEvents({
    click(e) {
      const { lat, lng } = e.latlng;
      setPosition([lat, lng]);
      
      // Reverse geocode to get address
      reverseGeocode(lat, lng).then(address => {
        onLocationSelect(lat, lng, address);
      });
    },
  });

  return position ? <Marker position={position} /> : null;
}

// Component to update map view when coordinates change
function MapViewUpdater({ lat, lng, zoom }: { lat?: number; lng?: number; zoom?: number }) {
  const map = useMap();
  
  useEffect(() => {
    if (lat && lng) {
      map.setView([lat, lng], zoom || 15);
    }
  }, [lat, lng, zoom, map]);
  
  return null;
}

// Cyrillic to Latin transliteration
const cyrillicToLatinMap: { [key: string]: string } = {
  'а': 'a', 'б': 'b', 'в': 'v', 'г': 'g', 'д': 'd', 'ђ': 'đ', 'е': 'e', 'ж': 'ž',
  'з': 'z', 'и': 'i', 'ј': 'j', 'к': 'k', 'л': 'l', 'љ': 'lj', 'м': 'm', 'н': 'n',
  'њ': 'nj', 'о': 'o', 'п': 'p', 'р': 'r', 'с': 's', 'т': 't', 'ћ': 'ć', 'у': 'u',
  'ф': 'f', 'х': 'h', 'ц': 'c', 'ч': 'č', 'џ': 'dž', 'ш': 'š',
  'А': 'A', 'Б': 'B', 'В': 'V', 'Г': 'G', 'Д': 'D', 'Ђ': 'Đ', 'Е': 'E', 'Ж': 'Ž',
  'З': 'Z', 'И': 'I', 'Ј': 'J', 'К': 'K', 'Л': 'L', 'Љ': 'Lj', 'М': 'M', 'Н': 'N',
  'Њ': 'Nj', 'О': 'O', 'П': 'P', 'Р': 'R', 'С': 'S', 'Т': 'T', 'Ћ': 'Ć', 'У': 'U',
  'Ф': 'F', 'Х': 'H', 'Ц': 'C', 'Ч': 'Č', 'Џ': 'Dž', 'Ш': 'Š'
};

function cyrillicToLatin(text: string): string {
  return text.split('').map(char => cyrillicToLatinMap[char] || char).join('');
}

// Reverse geocode function - uses own PHP proxy to avoid AdBlock issues,
// falls back to direct Nominatim if proxy fails
async function reverseGeocode(lat: number, lng: number): Promise<string | undefined> {
  // Determine API base URL (same logic as api/client.ts)
  const isProduction = window.location.hostname !== '127.0.0.1' && window.location.hostname !== 'localhost';
  const apiBase = isProduction 
    ? `${window.location.origin}/api`
    : 'http://127.0.0.1/svet-nekretnina/php-version/api';

  // Try 1: Own PHP proxy (bypasses AdBlock)
  try {
    const proxyController = new AbortController();
    const proxyTimeout = setTimeout(() => proxyController.abort(), 10000);
    
    const proxyResponse = await fetch(
      `${apiBase}/geocode/reverse?lat=${lat}&lon=${lng}`,
      { signal: proxyController.signal }
    );
    
    clearTimeout(proxyTimeout);
    
    if (proxyResponse.ok) {
      const data = await proxyResponse.json();
      const displayName = cyrillicToLatin(data.display_name || '');
      return displayName;
    }
  } catch (error: any) {
    console.warn('Proxy geocode failed, trying direct Nominatim:', error.message);
  }

  // Try 2: Direct Nominatim (works if not blocked by extensions)
  try {
    const directController = new AbortController();
    const directTimeout = setTimeout(() => directController.abort(), 8000);
    
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1&accept-language=sr-Latn`,
      {
        headers: {
          'Accept-Language': 'sr-Latn,en',
          'User-Agent': 'SvojNaSvom/1.0 (https://svojnasvom.com; contact@svojnasvom.com)'
        },
        signal: directController.signal
      }
    );
    
    clearTimeout(directTimeout);
    
    if (response.ok) {
      const data = await response.json();
      const displayName = cyrillicToLatin(data.display_name || '');
      return displayName;
    }
  } catch (error: any) {
    if (error.name === 'AbortError') {
      console.warn('Direct Nominatim timed out (8s)');
    } else {
      console.error('Direct Nominatim error:', error);
    }
  }
  
  return undefined;
}

export function LocationPickerMap({ 
  initialLat, 
  initialLng, 
  onLocationSelect,
  height = '300px'
}: LocationPickerMapProps) {
  const { language } = useI18n();
  const [position, setPosition] = useState<[number, number] | null>(
    initialLat && initialLng ? [initialLat, initialLng] : null
  );
  const [isLocating, setIsLocating] = useState(false);
  const mapRef = useRef<L.Map | null>(null);

  // Update position when initial coords change
  useEffect(() => {
    if (initialLat && initialLng) {
      setPosition([initialLat, initialLng]);
    }
  }, [initialLat, initialLng]);

  // Get user's current location
  const getCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      alert(language === 'sr' 
        ? 'Geolokacija nije podržana u vašem pregledaču' 
        : 'Geolocation is not supported by your browser');
      return;
    }

    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        setPosition([latitude, longitude]);
        
        // Reverse geocode
        const address = await reverseGeocode(latitude, longitude);
        onLocationSelect(latitude, longitude, address);
        
        // Update map view
        if (mapRef.current) {
          mapRef.current.setView([latitude, longitude], 15);
        }
        
        setIsLocating(false);
      },
      (error) => {
        console.error('Geolocation error:', error);
        alert(language === 'sr' 
          ? 'Greška pri dobijanju lokacije. Proverite da li ste dozvolili pristup lokaciji.' 
          : 'Error getting location. Please check if you allowed location access.');
        setIsLocating(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000
      }
    );
  }, [language, onLocationSelect]);

  // Serbia center
  const defaultCenter: [number, number] = [44.0165, 21.0059];
  const center = position || defaultCenter;
  const zoom = position ? 15 : 7;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
          <MapPin className="w-4 h-4 inline mr-1" />
          {language === 'sr' ? 'Označi lokaciju na mapi' : 'Mark location on map'}
        </label>
        <button
          type="button"
          onClick={getCurrentLocation}
          disabled={isLocating}
          className="flex items-center gap-1 px-3 py-1 text-xs bg-amber-500 hover:bg-amber-600 text-slate-900 rounded-lg transition-colors disabled:opacity-50"
        >
          {isLocating ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <Navigation className="w-3 h-3" />
          )}
          {language === 'sr' ? 'Moja lokacija' : 'My location'}
        </button>
      </div>
      
      <div className="rounded-lg overflow-hidden border border-slate-300 dark:border-slate-600">
        <MapContainer 
          center={center} 
          zoom={zoom} 
          style={{ height, width: '100%', zIndex: 1 }}
          ref={mapRef}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
            url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
            subdomains="abcd"
            maxZoom={20}
          />
          <MapClickHandler 
            position={position} 
            setPosition={setPosition} 
            onLocationSelect={onLocationSelect}
          />
          {initialLat && initialLng && (
            <MapViewUpdater lat={initialLat} lng={initialLng} zoom={15} />
          )}
        </MapContainer>
      </div>
      
      <p className="text-xs text-slate-500 dark:text-slate-400">
        {language === 'sr' 
          ? 'Kliknite na mapu da označite tačnu lokaciju nekretnine ili koristite "Moja lokacija"' 
          : 'Click on the map to mark the exact property location or use "My location"'}
      </p>
      
      {position && (
        <p className="text-xs text-green-600 dark:text-green-400">
          ✓ {language === 'sr' ? 'Lokacija označena:' : 'Location marked:'} {position[0].toFixed(6)}, {position[1].toFixed(6)}
        </p>
      )}
    </div>
  );
}
